import type { AdminUserMetabaseCollection } from '@branditdev/mf-types'
import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { Redirect, useLocation } from 'react-router-dom'
import { Container } from 'reactstrap'
import { adminUserMetabaseCollectionsQueryOptions, adminUserPortalsQueryOptions } from './adminUserDashboard.queries'
import { getAdminPortalIdFromPathname, shouldRedirectToDefaultAdminPortal } from './adminUserDashboard.utils'
import AdminUserDashboardTabs from './AdminUserDashboardTabs'

export type AdminUserDashboardRenderContext = {
  activeCollection: AdminUserMetabaseCollection | null
  collectionId: string | null
  metabaseCollectionsError: string | null
  metabaseCollectionsLoading: boolean
  portalId: number | null
}

type AdminUserDashboardProps = {
  children: ReactNode | ((context: AdminUserDashboardRenderContext) => ReactNode)
}

const contentStyle = (isReportsPage: boolean) =>
  isReportsPage
    ? { padding: '20px', border: 'none', backgroundColor: '#fff' }
    : {
        padding: '20px',
        border: 'none',
        borderRadius: '0 0 0.25rem 0.25rem',
        backgroundColor: '#fff'
      }

const AdminUserDashboard = ({ children }: AdminUserDashboardProps) => {
  const location = useLocation()
  const isReportsPage = location.pathname.includes('/reports')
  const portalIdFromUrl = getAdminPortalIdFromPathname(location.pathname)
  const [selectedMetabaseCollectionId, setSelectedMetabaseCollectionId] = useState<string | null>(null)

  const adminPortalsQuery = useQuery({
    ...adminUserPortalsQueryOptions(),
    enabled: !isReportsPage
  })
  const metabaseCollectionsQuery = useQuery({
    ...adminUserMetabaseCollectionsQueryOptions(),
    enabled: isReportsPage
  })

  const adminPortals = adminPortalsQuery.data ?? []
  const metabaseCollections = metabaseCollectionsQuery.data ?? []
  const firstAdminPortalId = adminPortals[0]?.id ?? null
  const requestedPortalIsAccessible =
    portalIdFromUrl !== null && adminPortals.some(portal => portal.id === portalIdFromUrl)
  const accessiblePortalId = requestedPortalIsAccessible ? portalIdFromUrl : null
  const activePortalId = accessiblePortalId ?? firstAdminPortalId
  const selectedCollectionIsAvailable =
    selectedMetabaseCollectionId !== null &&
    metabaseCollections.some(collection => collection.external_collection_id === selectedMetabaseCollectionId)
  const activeMetabaseCollectionId = selectedCollectionIsAvailable
    ? selectedMetabaseCollectionId
    : (metabaseCollections[0]?.external_collection_id ?? null)

  const shouldRedirectToDefaultPortal =
    !isReportsPage &&
    adminPortalsQuery.isSuccess &&
    firstAdminPortalId !== null &&
    shouldRedirectToDefaultAdminPortal({
      pathname: location.pathname,
      requestedPortalIsAccessible
    })

  if (shouldRedirectToDefaultPortal) {
    return <Redirect to={`/admin/${firstAdminPortalId}/orders`} />
  }

  const activeCollection =
    isReportsPage && activeMetabaseCollectionId
      ? metabaseCollections.find(collection => collection.external_collection_id === activeMetabaseCollectionId) || null
      : null
  const metabaseCollectionsError = metabaseCollectionsQuery.isError ? 'Unable to load collections.' : null
  const adminPortalsError = adminPortalsQuery.isError ? 'Unable to load admin portals.' : null
  const shouldRenderAdminChildren = isReportsPage || requestedPortalIsAccessible
  const renderedChildren = (() => {
    if (!shouldRenderAdminChildren) return null
    if (typeof children !== 'function') return children

    return children({
      activeCollection,
      collectionId: isReportsPage ? activeMetabaseCollectionId : null,
      metabaseCollectionsLoading: metabaseCollectionsQuery.isLoading,
      metabaseCollectionsError,
      portalId: isReportsPage ? null : accessiblePortalId
    })
  })()

  return (
    <main className="main-content">
      <div className="app-container">
        <Container>
          <div className="nav nav-tabs" style={{ borderBottom: 'none', marginBottom: '0' }}>
            <AdminUserDashboardTabs
              activeCollectionId={activeMetabaseCollectionId}
              activePortalId={activePortalId}
              adminPortals={adminPortals}
              adminPortalsError={adminPortalsError}
              adminPortalsLoading={!isReportsPage && adminPortalsQuery.isLoading}
              isReportsPage={isReportsPage}
              metabaseCollections={metabaseCollections}
              metabaseCollectionsError={metabaseCollectionsError}
              metabaseCollectionsLoading={isReportsPage && metabaseCollectionsQuery.isLoading}
              onReportTabClick={setSelectedMetabaseCollectionId}
            />
          </div>

          <div style={contentStyle(isReportsPage)}>{renderedChildren}</div>
        </Container>
      </div>
    </main>
  )
}

export default AdminUserDashboard
